import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Baby, Clock, CheckCircle2, ShieldCheck, Sparkles, TrendingUp, ArrowRight, MessageCircle } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import FAQ from "@/components/site/FAQ";

const BOOK_URL = "https://portal.pivot1st.co.uk/book/2/class";

const afterFaqs = [
  {
    q: "What happens right after I book?",
    a: "You'll receive a confirmation with your class details. If anything's unclear or you'd like to double-check the right level for your child, just get in touch — we're always happy to help.",
  },
  {
    q: "What should we bring to the first lesson?",
    a: "Just swimwear and a towel. For little ones, a two-layer swim nappy — an inner absorbent layer plus a snug neoprene outer nappy. The neoprene outer is required until your child has been reliably toilet-trained (dry) for at least 6 months. Swim nappies are available at reception if you need them, and there's no need to bring a changing mat — we supply those.",
  },
  {
    q: "When should we arrive?",
    a: "Please arrive about 10 minutes before your class — or 15 minutes for your very first visit — so you have time to change and settle in calmly, with no rushing.",
  },
  {
    q: "My child is nervous — what if the first lesson is tricky?",
    a: "That's completely normal and absolutely fine. Our instructors are experienced with nervous and first-time swimmers, and we build trust gently at your child's own pace. There's never any pressure.",
  },
  {
    q: "Can I watch the lesson — or do I get in the water?",
    a: "It depends on the class. Our Baby & Pre-School classes are adult-accompanied, so you'll be right there in the water with your little one throughout — no watching from the side. For our independent classes — Baby Sharks, Sharks 1–3 and adult lessons — you can relax on our raised spectator platform, which gives a brilliant view of the whole pool.",
  },
];

const steps = [
  { icon: <Baby size={24} />, title: "Choose the right lesson", desc: "Pick the class that suits your child's age and stage." },
  { icon: <Clock size={24} />, title: "Pick a time that works", desc: "Choose a class time that suits you from our timetable." },
  { icon: <CheckCircle2 size={24} />, title: "Secure your place", desc: "Confirm your spot and get ready for that first lesson." },
];

const reassurance = [
  { icon: <ShieldCheck size={20} />, text: "Safety is always our priority" },
  { icon: <Sparkles size={20} />, text: "Confidence comes before technique" },
  { icon: <TrendingUp size={20} />, text: "Every child progresses at their own pace" },
];

export default function BookNow() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="book-page">
      <Seo
        title="Book Now — Swimming Lessons | Happy Tiddlers Swim School"
        description="Book your swimming lessons at Happy Tiddlers in Barton-le-Clay, Bedfordshire. Reserve your space for baby, children's or adult classes today."
        path="/book"
      />
      <Navbar />

      {/* Header */}
      <section className="relative overflow-hidden pt-16 pb-14 md:pt-24 md:pb-16" data-testid="book-header">
        <div className="blob absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#FF7A1A]/10" />
        <div className="container mx-auto px-6 md:px-12 relative max-w-3xl">
          <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Book Now</span>
          <h1 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
            Ready to begin your child's swimming journey?
          </h1>
          <p className="mt-6 text-lg text-[#334155] leading-relaxed">
            Booking with Happy Tiddlers is simple — and support is always available if you're unsure where to start.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="pb-16 md:pb-20" data-testid="book-steps">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="relative bg-white rounded-[2rem] p-8 md:p-10 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
                data-testid={`book-step-${i}`}
              >
                <span className="absolute top-8 right-8 font-heading text-5xl font-black text-[#0EA5E9]/10 leading-none">{i + 1}</span>
                <span className="grid place-items-center w-14 h-14 rounded-2xl mb-6 bg-[#0EA5E9] text-white">{s.icon}</span>
                <h3 className="font-heading text-xl font-semibold text-[#0F172A]">{s.title}</h3>
                <p className="mt-3 text-[#334155] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reassurance + CTAs */}
      <section className="pb-24 md:pb-32" data-testid="book-reassurance">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-[2.5rem] bg-[#0A2540] px-8 py-14 md:px-16 md:py-16 text-center"
          >
            <div className="blob absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#0EA5E9]/20" />
            <div className="blob absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#FF7A1A]/20" />
            <div className="relative">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Before you book, remember</h2>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                {reassurance.map((r) => (
                  <span key={r.text} className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-5 py-2.5 text-sm font-semibold text-white" data-testid="book-reassurance-item">
                    <span className="text-[#38BDF8]">{r.icon}</span>
                    {r.text}
                  </span>
                ))}
              </div>
              <p className="mt-8 text-lg text-[#CBD5E1]">If you have questions, we're here to help.</p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <a
                  href={BOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.6)] hover:-translate-y-1 transition-all duration-300"
                  data-testid="book-lessons-cta"
                >
                  Book lessons now
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white/10 text-white font-bold px-8 py-4 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300"
                  data-testid="book-contact-cta"
                >
                  <MessageCircle size={18} /> Contact us for advice
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <FAQ
        items={afterFaqs}
        heading="What happens after I book?"
        intro="A few things to put your mind at ease before that first lesson."
      />

      <Footer />
    </div>
  );
}
